import { Button, DialogDescription, DialogHeader, DialogTitle, Icon } from "@nexus/ui"
import { useNavigate } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import { partnerSendsOf } from "@/features/marketplace/lib/sent-scripts-map"
import type { SentScriptsMap } from "@/features/marketplace/types/marketplace.types"

function summarizeAlreadySent(scriptIds: string[], sentScriptsMap: SentScriptsMap) {
  // Every partner order, not just the newest, so an item held by A and B never reads as B-only.
  const metas = scriptIds.flatMap((id) => partnerSendsOf(sentScriptsMap[id]))

  const vendorNames = [...new Set(metas.map((m) => m.vendorName))]
  const orderIds = [...new Set(metas.map((m) => m.orderId))]

  return {
    metas,
    singleVendorName: vendorNames.length === 1 ? vendorNames[0] : null,
    singleOrderId: orderIds.length === 1 ? orderIds[0] : null,
    hasSeveralPartners: vendorNames.length > 1,
    count: scriptIds.length,
  }
}

export interface SendAlreadySentViewProps {
  scriptIds: string[]
  sentScriptsMap: SentScriptsMap
  onClose: () => void
}

export function SendAlreadySentView({
  scriptIds,
  sentScriptsMap,
  onClose,
}: SendAlreadySentViewProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { singleVendorName, singleOrderId, hasSeveralPartners, count } = summarizeAlreadySent(scriptIds, sentScriptsMap)

  function handleViewOrder() {
    if (!singleOrderId) return
    onClose()
    void navigate({ to: "/marketplace", search: { tab: "in-production", order: singleOrderId } })
  }

  const bodyKey = hasSeveralPartners
    ? `opportunities.scripts.sendDialog.allAlreadySentBody_allPartners_${count === 1 ? "one" : "other"}`
    : count === 1 && singleVendorName
      ? "opportunities.scripts.sendDialog.allAlreadySentBody_one"
      : singleVendorName
        ? "opportunities.scripts.sendDialog.allAlreadySentBody_sameVendor"
        : "opportunities.scripts.sendDialog.allAlreadySentBody_other"

  return (
    <div className="flex flex-col items-center px-2 pt-2 pb-1 text-center">
      <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
        <Icon name="check-circle" size="lg" className="text-success" />
      </span>

      <DialogHeader className="items-center gap-1.5">
        <DialogTitle className="text-base font-medium">
          {t("opportunities.scripts.sendDialog.allAlreadySentTitle")}
        </DialogTitle>
        <DialogDescription className="max-w-[20rem] text-sm leading-relaxed">
          {t(bodyKey, { count, vendorName: singleVendorName ?? "" })}
        </DialogDescription>
      </DialogHeader>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
        {singleOrderId && (
          <Button size="sm" onClick={handleViewOrder}>
            {t("opportunities.scripts.sentCard.viewOrder")}
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={onClose}>
          {t("common.close")}
        </Button>
      </div>
    </div>
  )
}
